import type { Chapter, Phase } from './types.js';
import { JUMP_RUN } from './screens.js';

const MIN = 60_000;
const SEC = 1_000;

export interface ChapterSpec {
  chapter: Chapter;
  /** Local wall-clock start, from the run of show in 03-SHOW-DATA.md. */
  startsAt: string;
  lengthMs: number;
  label: string;
  era: string;
  /** The room look, for the console's run-of-show list. */
  room: string;
}

/**
 * Run of show — three hours, one seating (03-SHOW-DATA.md).
 * 16:45 doors · 17:00 depart · 17:30 · 18:05 · 18:40 · 19:15 · 19:45 docking.
 */
export const RUN_OF_SHOW: readonly ChapterSpec[] = [
  {
    chapter: 0,
    startsAt: '16:45',
    lengthMs: 15 * MIN,
    label: 'BOARDING',
    era: 'boarding',
    room: 'house light, ambient playlist',
  },
  {
    chapter: 1,
    startsAt: '17:00',
    lengthMs: 30 * MIN,
    label: 'CH.1 · EARTH',
    era: '2026',
    room: 'blue, clean',
  },
  {
    chapter: 2,
    startsAt: '17:30',
    lengthMs: 35 * MIN,
    label: 'CH.2 · BOOM',
    era: '2213',
    room: 'magenta / cyan',
  },
  {
    chapter: 3,
    startsAt: '18:05',
    lengthMs: 35 * MIN,
    label: 'CH.3 · THE WAR',
    era: '2398',
    room: 'red alert',
  },
  {
    chapter: 4,
    startsAt: '18:40',
    lengthMs: 35 * MIN,
    label: 'CH.4 · UNDERGROUND',
    era: '2617',
    room: 'amber / rust',
  },
  {
    chapter: 5,
    startsAt: '19:15',
    lengthMs: 30 * MIN,
    label: 'CH.5 · SURFACE',
    era: '2791',
    room: 'green / dawn',
  },
] as const;

export const DOCKING_AT = '19:45';

export function chapterSpec(chapter: Chapter): ChapterSpec {
  const spec = RUN_OF_SHOW[chapter];
  if (!spec) throw new Error(`no run-of-show entry for chapter ${chapter}`);
  return spec;
}

export function chapterLengthMs(chapter: Chapter): number {
  return chapterSpec(chapter).lengthMs;
}

/**
 * The phase a chapter opens in.
 *
 * Every chapter is *arrived at* by jumping, so a chapter opens on `jump` and the TV
 * walks it onward: jump.mp4 (8 s) → hard cut → era intro (10 s) → the chapter scene
 * (03-SHOW-DATA.md). Opening straight on `serve` would skip both films.
 */
export function openingPhase(chapter: Chapter): Phase {
  return chapter === 0 ? 'boarding' : 'jump';
}

/**
 * The beats inside a chapter, with the clock.
 *
 * Not every chapter runs the same beats. Food lands after the first, third and last
 * cocktail — so chapters 1, 3 and 5 carry a `course` and chapters 2 and 4 do not (the reindeer
 * went to 2617 and back to 2791 on 2026-09-27). And
 * chapter 1 alone carries `photo`: the last picture on Earth, called from the TV in
 * the minutes before the crew pick up their phones and charge the drive. After that
 * the ship is not on Earth any more, and the moment cannot be had again.
 *
 * Every era opens on its role step after the films: each phone holds to see whether it is a saboteur
 * this stop (2026-10-09). The games are an era's last beat, and they are the charge (Marek,
 * 2026-09-28): each game done fills the ship. Once all are done the stop's own steps follow — the
 * alarms, the meeting, the vote, the verdict, the reveal — each on the clicker's NEXT, and nothing
 * moves on by itself (Marek, 2026-10-09: "full control over the evening with the clicker").
 *
 * `atMs` is an offset from the chapter's own start, so the whole run of show moves
 * together if a chapter starts late.
 */
export interface Beat {
  phase: Phase;
  atMs: number;
  /** What the operator calls it. */
  label: string;
  /** What is actually happening in the room. */
  note: string;
}

const BEATS: Record<Chapter, readonly Beat[]> = {
  0: [{ phase: 'boarding', atMs: 0, label: 'BOARDING', note: 'doors, passes, seats' }],
  1: [
    { phase: 'jump', atMs: 0, label: 'LAUNCH', note: 'jump film · 8s' },
    { phase: 'intro', atMs: 12 * SEC, label: 'ERA INTRO', note: '2026 · 10s' },
    { phase: 'role', atMs: 75 * SEC, label: 'ROLE', note: 'every phone: hold to see your role · saboteur or crew' },
    { phase: 'serve', atMs: 2 * MIN, label: 'POUR 1', note: 'LAST HORIZON to the table' },
    {
      phase: 'course',
      atMs: 12 * MIN,
      label: 'COURSE 1',
      note: 'DISH 1 · passed from the kitchen',
    },
    {
      phase: 'photo',
      atMs: 20 * MIN,
      label: 'LAST PICTURE ON EARTH',
      note: 'the captain’s selfie · 90s · once only',
    },
    {
      phase: 'fieldwork',
      atMs: 22 * MIN,
      label: 'GAMES',
      note: 'every game done charges the ship · then the jump',
    },
  ],
  2: [
    { phase: 'jump', atMs: 0, label: 'JUMP', note: 'jump film · 8s' },
    { phase: 'intro', atMs: 12 * SEC, label: 'ERA INTRO', note: '2213 · 10s' },
    { phase: 'role', atMs: 75 * SEC, label: 'ROLE', note: 'every phone: hold to see your role · saboteur or crew' },
    { phase: 'serve', atMs: 2 * MIN, label: 'POUR 2', note: 'NEON GARDEN to the table' },
    { phase: 'signal', atMs: 9 * MIN, label: 'HIDDEN SIGNAL', note: 'the phones call the hunt · nothing on the TV' },
    {
      phase: 'fieldwork',
      atMs: 12 * MIN,
      label: 'GAMES',
      note: 'every game done charges the ship · the machines rise at the end',
    },
  ],
  3: [
    { phase: 'jump', atMs: 0, label: 'JUMP', note: 'jump film · 8s' },
    { phase: 'intro', atMs: 12 * SEC, label: 'ERA INTRO', note: '2398 · 10s' },
    { phase: 'role', atMs: 75 * SEC, label: 'ROLE', note: 'every phone: hold to see your role · saboteur or crew' },
    { phase: 'serve', atMs: 2 * MIN, label: 'POUR 3', note: 'the third pour to the table' },
    { phase: 'course', atMs: 12 * MIN, label: 'COURSE 2', note: 'DISH 2 · rationed, on purpose' },
    {
      phase: 'fieldwork',
      atMs: 19 * MIN,
      label: 'GAMES',
      note: 'every game done charges the ship · then the jump',
    },
  ],
  4: [
    { phase: 'jump', atMs: 0, label: 'JUMP', note: 'jump film · 8s' },
    { phase: 'intro', atMs: 12 * SEC, label: 'ERA INTRO', note: '2617 · 10s' },
    { phase: 'role', atMs: 75 * SEC, label: 'ROLE', note: 'every phone: hold to see your role · saboteur or crew' },
    { phase: 'serve', atMs: 2 * MIN, label: 'POUR 4', note: 'the fourth pour to the table' },
    {
      phase: 'fieldwork',
      atMs: 12 * MIN,
      label: 'GAMES',
      note: 'pass it on at the table first · every game done charges the ship',
    },
  ],
  5: [
    { phase: 'jump', atMs: 0, label: 'JUMP', note: 'jump film · 8s' },
    { phase: 'intro', atMs: 12 * SEC, label: 'ERA INTRO', note: '2791 · 10s' },
    { phase: 'role', atMs: 75 * SEC, label: 'ROLE', note: 'every phone: hold to see your role · saboteur or crew' },
    { phase: 'serve', atMs: 2 * MIN, label: 'POUR 5', note: 'the last pour to the table' },
    { phase: 'course', atMs: 15 * MIN, label: 'COURSE 3', note: 'DISH 3 · the reindeer, back on open land' },
    {
      phase: 'fieldwork',
      atMs: 19 * MIN,
      label: 'GAMES',
      note: 'plant at the table first · every game done charges the ship home',
    },
    { phase: 'farewell', atMs: 26 * MIN, label: 'DOCKING', note: 'chronicle cards · 19:45' },
  ],
};

export function beatsFor(chapter: Chapter): readonly Beat[] {
  return BEATS[chapter] ?? [];
}

/** The beats a chapter actually runs, in order. */
export function phaseOrder(chapter: Chapter): readonly Phase[] {
  return beatsFor(chapter).map((b) => b.phase);
}

/** Kept for the console's beat list: chapter 1 runs every beat there is. */
export const CHAPTER_PHASES: readonly Phase[] = phaseOrder(1);

/**
 * The next beat in this chapter, or null at the end of it.
 *
 * Chapter-aware because the chapters differ: pressing Space after the pour lands on
 * the course in chapters 1, 3 and 5 and on the stations in 2 and 4.
 */
export function nextPhase(phase: Phase, chapter: Chapter = 1): Phase | null {
  const order = phaseOrder(chapter);
  const i = order.indexOf(phase);
  if (i === -1 || i === order.length - 1) return null;
  return order[i + 1] ?? null;
}

/**
 * When the run of show plans to leave the era the room is in (Marek, 2026-09-28: "announce, then
 * move on at the planned time"): the evening's launch plus the planned lengths of every era so far,
 * so an era that ran late is followed by one that catches up. Time the captain held the show is
 * added back. A room with no launch on record plans from its own era's start.
 */
export function plannedEraEnd(
  state: { chapter: Chapter; chapterStartedAt: number; chapterLengthMs: number; hold: { accumulatedMs: number }; launchedAt?: number | null },
): number {
  const held = state.hold.accumulatedMs;
  if (state.chapter < 1) return state.chapterStartedAt + state.chapterLengthMs + held;
  if (state.launchedAt === null || state.launchedAt === undefined) return state.chapterStartedAt + state.chapterLengthMs + held;
  let end = state.launchedAt;
  for (let c = 1; c <= state.chapter; c++) end += chapterLengthMs(c as Chapter);
  return end + held;
}

/** Wall-clock time a beat is due, as `HH:MM`. */
export function beatClock(chapter: Chapter, beat: Pick<Beat, 'atMs'>): string {
  const spec = chapterSpec(chapter);
  const [h, m] = spec.startsAt.split(':').map(Number);
  const total = (h ?? 0) * 60 + (m ?? 0) + Math.round(beat.atMs / MIN);
  const hh = Math.floor(total / 60) % 24;
  const mm = total % 60;
  return `${String(hh).padStart(2, '0')}:${String(mm).padStart(2, '0')}`;
}

/**
 * What one press of the presentation clicker does.
 *
 * The run of show as a slideshow: every click is the next beat, and the click after the
 * last beat of a chapter is the jump. It is the same order Space walks, but it does not
 * wrap — pressing on after the charge used to land back on the pour of the same
 * chapter, which from a clicker in the dark is a show going backwards.
 */
export type Step =
  | { kind: 'phase'; phase: Phase }
  /** Boarding → chapter 1. */
  | { kind: 'launch' }
  /** The end of a chapter → the next one: lights, film, music, the lot. */
  | { kind: 'jump' }
  /** The end of chapter 5 → the farewell and the chronicle. */
  | { kind: 'dock' }
  /** Docked: every phone opens the guest's evening, wrapped — the clicker's last step. */
  | { kind: 'wrapped' }
  /** Back into the era before, onto its last slide: its lights and playlist, no film, no jump song. */
  | { kind: 'era'; chapter: Chapter; phase: Phase };

/** The films. The clicker never steps back into one — they would play again. */
const FILM_PHASES: ReadonlySet<Phase> = new Set<Phase>(['jump', 'intro']);

export function stepFrom(chapter: Chapter, phase: Phase, direction: 'next' | 'back'): Step | null {
  if (chapter === 0) return direction === 'next' ? { kind: 'launch' } : null;
  // Docked: the Wrapped is ahead, and back undocks onto the last slide of the last era.
  if (phase === 'farewell') return direction === 'back' ? lastSlideOf(5) : { kind: 'wrapped' };

  // Farewell is reached by docking, which fires the chronicle; setting it as a phase
  // would skip that. So it is not a step in its own right.
  const order = phaseOrder(chapter).filter((p) => p !== 'farewell');
  const i = order.indexOf(phase);
  if (i === -1) return null;

  if (direction === 'back') {
    // One slide back within the era. From its first slide, or from a film on the way in, back into
    // the era before, onto its last slide. Never into a film: it would play again.
    const previous = order[i - 1];
    if (previous && !FILM_PHASES.has(previous)) return { kind: 'phase', phase: previous };
    // Never from the first era back to boarding: that starts the evening afresh, and is the Mac's
    // BOARDING square alone, which asks first (Marek, 2026-10-09).
    if (chapter === 1) return null;
    return lastSlideOf((chapter - 1) as Chapter);
  }

  const following = order[i + 1];
  if (following) return { kind: 'phase', phase: following };
  return chapter === 5 ? { kind: 'dock' } : { kind: 'jump' };
}

/** The last slide of an era: where BACK lands when it steps into that era. */
function lastSlideOf(chapter: Chapter): Step {
  const slides = phaseOrder(chapter).filter((p) => !FILM_PHASES.has(p) && p !== 'farewell');
  return { kind: 'era', chapter, phase: slides[slides.length - 1] ?? 'boarding' };
}

/**
 * How long a film holds the clicker off, so a double-press cannot skip it, when there is no TV to
 * ask. With the TV open the clicker waits for as long as the TV says the film is playing, however
 * long it is (apps/desktop `tv/filmClock.ts`). The jump runs its 16 s across the wall and the
 * table; an era film is narrated, and a stray press can never cut the narrator off mid-sentence. Once
 * it ends the TV holds on the era until NEXT takes the role step (2026-10-09).
 */
export function filmHoldMs(phase: Phase): number {
  if (phase === 'jump') return JUMP_RUN.settledMs;
  if (phase === 'intro') return 60_000;
  return 0;
}

/**
 * One square of an era, as both consoles draw it (the Mac's strip, the phone's grid) and as
 * both progress bars count it.
 *
 * A square is either a part of the era the show can be put in by hand, or the move that ends
 * the era: LAUNCH out of boarding, JUMP to the next era, DOCK into the chronicle. The films are
 * neither. They play on the way into an era, and nobody sends the show back into one.
 */
export type EraPart =
  | { kind: 'phase'; phase: Phase; name: string; label: string; atMs: number }
  | { kind: 'launch' | 'jump' | 'dock'; name: string; label: string; atMs: number };

export function eraParts(chapter: Chapter): readonly EraPart[] {
  const parts = beatsFor(chapter)
    .filter((b) => !FILM_PHASES.has(b.phase) && b.phase !== 'farewell')
    .map((b): EraPart => ({ kind: 'phase', phase: b.phase, name: b.phase, label: b.label, atMs: b.atMs }));
  // The move out lands when the next era is due, so its time reads as that era's start.
  const end = chapterLengthMs(chapter);
  if (chapter === 0) parts.push({ kind: 'launch', name: 'launch', label: `year ${chapterSpec(1).era}`, atMs: end });
  else if (chapter === 5) parts.push({ kind: 'dock', name: 'dock', label: 'chronicle', atMs: end });
  else {
    const next = chapterSpec((chapter + 1) as Chapter);
    parts.push({ kind: 'jump', name: 'jump', label: `year ${next.era}`, atMs: end });
  }
  return parts;
}

/** The parts of an era the show can be in: the ticks a progress bar fills, without the move out. */
export function eraSteps(chapter: Chapter): readonly EraPart[] {
  return eraParts(chapter).filter((p) => p.kind === 'phase');
}

/**
 * The square that is lit: the part the show is in, the DOCK square once docked, and none (-1)
 * while a film plays on the way in.
 */
export function eraPartIndex(chapter: Chapter, phase: Phase): number {
  const parts = eraParts(chapter);
  if (phase === 'farewell') return parts.findIndex((p) => p.kind === 'dock');
  return parts.findIndex((p) => p.kind === 'phase' && p.phase === phase);
}
